<template>
  <v-container class="py-8">
    <h1 class="text-primary mb-2">En este dispositivo</h1>
    <p class="text-body-1 text-grey-darken-1 mb-6">
      Proyectos guardados para seguir escribiendo sin conexión.
    </p>

    <EmptyList
      v-if="!isLoading && deviceProjects.length === 0"
      icon="mdi-cellphone-arrow-down"
      title="No hay copias en este dispositivo"
      description="Abre un proyecto y elige Guardar en este dispositivo."
      hide-button
    />

    <v-card
      v-for="project in deviceProjects"
      :key="project.projectId"
      class="mb-3 pa-4"
      variant="outlined"
    >
      <div class="d-flex flex-wrap align-center ga-4">
        <div class="flex-grow-1">
          <div class="text-h6">{{ project.title }}</div>
          <div class="text-body-2 text-medium-emphasis">
            Respaldo: {{ formatBackupDate(project.backupAt) }}
          </div>
          <div class="text-body-2 text-medium-emphasis">
            Último cambio: {{ formatChangeDate(project.lastServerChangeAt) }}
          </div>
          <div class="text-body-2 text-medium-emphasis">
            {{ chapterLabel(project.chapterCount) }}
          </div>
          <div v-if="project.hasPendingChanges" class="text-body-2 text-warning">
            Hay cambios sin subir
          </div>
        </div>
        <div class="d-flex flex-wrap ga-2">
          <v-btn
            color="primary"
            variant="outlined"
            prepend-icon="mdi-cloud-sync-outline"
            :disabled="!isOnline || removingId === project.projectId"
            :loading="syncingId === project.projectId"
            @click="syncProject(project.projectId)"
          >
            Sincronizar
          </v-btn>
          <v-btn
            color="error"
            variant="text"
            prepend-icon="mdi-delete-outline"
            :disabled="syncingId === project.projectId"
            :loading="removingId === project.projectId"
            @click="askRemove(project)"
          >
            Quitar
          </v-btn>
        </div>
      </div>
    </v-card>

    <SyncFailureModal
      v-model:show="showSyncFailureModal"
      :project-id="syncFailure?.projectId || ''"
      :project-title="syncFailure?.projectTitle || ''"
      :synced-count="syncFailure?.syncedCount || 0"
      :total-count="syncFailure?.totalCount || 0"
      :failures="syncFailure?.failures || []"
    />

    <DeleteModal
      v-model:show="showRemoveModal"
      title="Quitar copia"
      :description="removeDescription"
      :alert-text="removeAlertText"
      :document-name="removeTarget?.title || null"
      confirm-button-text="Quitar"
      @confirm="confirmRemove"
      @cancel="hideRemoveModal"
    />
  </v-container>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'

import DeleteModal from '@/components/baseComponents/DeleteModal.vue'
import EmptyList from '@/components/baseComponents/EmptyList.vue'
import SyncFailureModal from '@/components/modals/SyncFailureModal.vue'
import { useConnectionStatus } from '@/composables/useConnectionStatus'
import { useOfflineLibrary } from '@/composables/useOfflineLibrary'
import { useToast } from '@/composables/useToast'
import { useCurrentChapterStore } from '@/stores/currentChapter'
import {
  deleteLocalProjectCopy,
  syncOfflineProject,
  type DeviceProjectCopy,
  type SyncChapterFailure,
} from '@/services/offlineLibrary'

function formatTimestamp(value: number | string): string {
  if (!value) return 'Sin fecha'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Sin fecha'
  return date.toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' })
}

export default defineComponent({
  name: 'LocalCopiesView',

  components: {
    DeleteModal,
    EmptyList,
    SyncFailureModal,
  },

  setup() {
    const { isOnline } = useConnectionStatus()
    const { deviceProjects, refreshDeviceProjects } = useOfflineLibrary()
    const { triggerToast } = useToast()
    const currentChapterStore = useCurrentChapterStore()
    const isLoading = ref(true)
    const syncingId = ref('')
    const removingId = ref('')
    const showRemoveModal = ref(false)
    const removeTarget = ref<DeviceProjectCopy | null>(null)
    const syncFailure = ref<{
      projectId: string
      projectTitle: string
      syncedCount: number
      totalCount: number
      failures: SyncChapterFailure[]
    } | null>(null)
    const showSyncFailureModal = computed({
      get: () => syncFailure.value !== null,
      set: (open: boolean) => {
        if (!open) syncFailure.value = null
      },
    })

    const removeAlertText = computed(() => (
      removeTarget.value?.hasPendingChanges
        ? 'Hay cambios sin subir que se perderán en'
        : 'Se quitará la copia de'
    ))

    const removeDescription = computed(() => (
      removeTarget.value?.hasPendingChanges
        ? 'Si quitas esta copia, los cambios que todavía no se subieron no se podrán recuperar. Esta acción no se puede deshacer.'
        : '¿Estás seguro de que quieres quitar esta copia de este dispositivo? Esta acción no se puede deshacer.'
    ))

    onMounted(async () => {
      await refreshDeviceProjects()
      isLoading.value = false
    })

    const askRemove = (project: DeviceProjectCopy) => {
      if (removingId.value || syncingId.value) return
      removeTarget.value = project
      showRemoveModal.value = true
    }

    const hideRemoveModal = () => {
      showRemoveModal.value = false
      removeTarget.value = null
    }

    const confirmRemove = async () => {
      const projectId = removeTarget.value?.projectId
      if (!projectId) return
      await removeProject(projectId)
      removeTarget.value = null
    }

    const removeProject = async (projectId: string) => {
      if (removingId.value || syncingId.value) return
      removingId.value = projectId
      try {
        await deleteLocalProjectCopy(projectId)
        await refreshDeviceProjects()
        triggerToast({
          message: 'Se quitó la copia de este dispositivo',
          color: 'success',
        })
      } catch (error) {
        console.error('Error removing local project copy:', error)
        triggerToast({
          message: 'No se pudo quitar la copia local',
          color: 'error',
        })
      } finally {
        removingId.value = ''
      }
    }

    const syncProject = async (projectId: string) => {
      if (!isOnline.value || syncingId.value) return
      syncingId.value = projectId
      try {
        const result = await syncOfflineProject(projectId)
        await refreshDeviceProjects()
        if (result.failedChapters.length === 0) {
          currentChapterStore.applySyncedDeviceChapter(projectId)
        }
        if (result.failedChapters.length > 0) {
          const project = deviceProjects.value.find((item) => item.projectId === projectId)
          syncFailure.value = {
            projectId,
            projectTitle: project?.title || '',
            syncedCount: result.attempted - result.failedChapters.length,
            totalCount: result.attempted,
            failures: result.failedChapters,
          }
          return
        }
        triggerToast({
          message: 'La copia local quedó sincronizada',
          color: 'success',
        })
      } catch (error) {
        console.error('Error syncing offline project:', error)
        triggerToast({
          message: 'No se pudo sincronizar el proyecto',
          color: 'error',
        })
      } finally {
        syncingId.value = ''
      }
    }

    return {
      isOnline,
      deviceProjects,
      isLoading,
      syncingId,
      removingId,
      showRemoveModal,
      showSyncFailureModal,
      syncFailure,
      removeTarget,
      removeAlertText,
      removeDescription,
      syncProject,
      askRemove,
      hideRemoveModal,
      confirmRemove,
      formatBackupDate: (value: number) => formatTimestamp(value),
      formatChangeDate: (value: string) => formatTimestamp(value),
      chapterLabel: (count: number) => (
        count === 1 ? '1 capítulo' : `${count} capítulos`
      ),
    }
  },
})
</script>
